import qrcode from './qrCode';
import layout from './uidLayout.json';

export const CERTIFICATIONS = ['None', 'CCC', 'CE', 'FCC', 'DNV'];
export const INSTRUCTION = {
  documentNumber: 'I-26115 000 001',
  revision: 'A1',
  name: 'Generic Wave XL MON laser engraving',
};
export const TEMPLATE_VERSION = `${INSTRUCTION.documentNumber} ${INSTRUCTION.revision}`;
export const SERVICE_URL = 'https://service.northinvent.com';

export function supportsTemplateVersion(version) {
  // The instruction identity was corrected; both identifiers use this same artwork.
  return version === TEMPLATE_VERSION || version === 'I-26115 000 002 A1';
}

export function websiteUrlForRecord({ deviceUrl, deviceId, isSandbox = false } = {}) {
  if (isSandbox) {
    const match = typeof deviceUrl === 'string' && deviceUrl.match(/^https:\/\/service\.northinvent\.com\/(?:device\/test\/|quality\/laser-engraving\?t=)([0-9a-f]{24})$/);
    if (!match || typeof deviceId !== 'string' || match[1] !== deviceId) {
      throw new Error('The saved test link is invalid or does not match this test label. Reopen the label in the Test family.');
    }
    return deviceUrl;
  }
  if (deviceUrl === undefined) return SERVICE_URL;
  const match = typeof deviceUrl === 'string' && deviceUrl.match(/^https:\/\/service\.northinvent\.com\/device\/([0-9a-f]{24})$/);
  if (!match || typeof deviceId !== 'string' || match[1] !== deviceId) {
    throw new Error('The saved device link is invalid or does not match this device. Contact North Invent before downloading or printing this label.');
  }
  return deviceUrl;
}

function escapeXml(value) {
  return String(value).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[ch]));
}

export function qrArtwork(payload, slot) {
  const qr = qrcode(0, 'M');
  qr.addData(payload, /^[0-9A-Z $%*+\-./:]+$/.test(payload) ? 'Alphanumeric' : 'Byte');
  qr.make();
  const count = qr.getModuleCount();
  const pitch = slot.size / (count + 8);
  let path = '';
  for (let row = 0; row < count; row += 1) {
    for (let column = 0; column < count; column += 1) {
      if (qr.isDark(row, column)) path += `M${column + 4},${row + 4}h1v1h-1z`;
    }
  }
  return `<g data-field="qr" data-payload="${escapeXml(payload)}" data-model="2" data-error-correction="M" data-quiet-zone-modules="4" data-module-mm="${pitch}" transform="translate(${slot.x} ${slot.y})"><rect width="${slot.size}" height="${slot.size}" fill="white"/><path transform="scale(${pitch})" d="${path}" fill="black" stroke="none"/></g>`;
}

function outlineText(value, slot, field) {
  const glyphs = {
    ...layout.font.glyphs,
    T: { path: 'M0.15,0.802018H2.75V1.314648H1.756V3.832324H1.144V1.314648H0.15Z', bounds: [0.15, 0.802018, 2.6, 3.030306], advance: 2.9 },
    '-': { path: 'M0.2,2.05H1.45V2.56H0.2Z', bounds: [0.2, 2.05, 1.25, 0.51], advance: 1.65 },
  };
  function measure(text, gap) {
    let x = 0;
    let left = Infinity;
    let right = -Infinity;
    let top = Infinity;
    let bottom = -Infinity;
    for (const character of text) {
      const glyph = glyphs[character];
      if (!glyph) throw new Error('The stored label has an unsupported character.');
      const bounds = glyph.bounds;
      if (bounds[2] > 0) {
        left = Math.min(left, x + bounds[0]);
        right = Math.max(right, x + bounds[0] + bounds[2]);
        top = Math.min(top, bounds[1]);
        bottom = Math.max(bottom, bounds[1] + bounds[3]);
      }
      x += glyph.advance + gap;
    }
    return { left, right, top, width: right - left, height: bottom - top };
  }
  const reference = measure(slot.referenceText, 0);
  const gap = (slot.width - reference.width) / (slot.referenceText.length - 1);
  const actual = measure(value, gap);
  const scaleX = slot.maxWidth ? Math.min(1, slot.maxWidth / actual.width) : 1;
  const scaleY = slot.height / reference.height;
  const xOffset = slot.centerX - (actual.left + actual.right) * scaleX / 2;
  const yOffset = slot.top - reference.top * scaleY;
  let x = 0;
  let paths = '';
  for (const character of value) {
    const glyph = glyphs[character];
    if (glyph.path) paths += `<path transform="translate(${x} 0)" d="${glyph.path}"/>`;
    x += glyph.advance + gap;
  }
  return `<g data-field="${field}" aria-label="${escapeXml(value)}" transform="translate(${xOffset} ${yOffset}) scale(${scaleX} ${scaleY})" fill="none" stroke="black" stroke-width="${layout.stroke}" stroke-linejoin="round">${paths}</g>`;
}

function certificationArtwork(name, index) {
  const asset = layout.certifications.assets[name];
  const slot = layout.certifications.slots[index];
  if (!asset || !slot) return '';
  const scale = Math.min(1, slot.maxWidth / asset.width, slot.maxHeight / asset.height);
  const x = slot.centerX - asset.width * scale / 2;
  const y = slot.centerY - asset.height * scale / 2;
  return `<g data-field="certification" data-slot="${index + 1}" data-certification="${name}" transform="translate(${x} ${y}) scale(${scale})"><path d="${asset.paths.join('')}" fill="black" stroke="none"/></g>`;
}

export function certificationPreview(name) {
  const asset = layout.certifications.assets[name];
  return asset ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${asset.width} ${asset.height}"><path d="${asset.paths.join('')}" fill="black"/></svg>` : '';
}

export function createUidSvg({ productNumber = '', serviceTagNumber = '', certifications = [], deviceUrl, deviceId, isSandbox = false } = {}) {
  if (serviceTagNumber && isSandbox !== serviceTagNumber.startsWith('TEST-')) throw new Error('The label identity does not match its production or Test environment.');
  const fixed = `<g fill="none" stroke="black" stroke-width="${layout.stroke}" stroke-linejoin="round"><path data-field="border" d="${layout.borderPath}"/><path data-field="brand" d="${layout.brandPath}"/><path data-field="manufacturer" d="${layout.manufacturerPath}"/></g>`;
  const product = productNumber ? outlineText(productNumber, layout.productSlot, 'product') : '<rect x="166.4" y="3" width="33" height="3.1" fill="#eef2f5"/>';
  const serviceSlot = isSandbox ? { ...layout.serviceSlot, maxWidth: layout.serviceSlot.width } : layout.serviceSlot;
  const service = serviceTagNumber ? outlineText(`SERVICE: ${serviceTagNumber}`, serviceSlot, 'service') + qrArtwork(serviceTagNumber, layout.serviceQr) : `<rect x="${layout.serviceQr.x}" y="${layout.serviceQr.y}" width="${layout.serviceQr.size}" height="${layout.serviceQr.size}" fill="#eef2f5"/><rect x="158.6" y="11" width="48.8" height="3.1" fill="#eef2f5"/>`;
  const websiteUrl = serviceTagNumber ? websiteUrlForRecord({ deviceUrl, deviceId, isSandbox }) : '';
  const website = websiteUrl ? qrArtwork(websiteUrl, layout.websiteQr) : `<rect data-field="website-qr-pending" x="${layout.websiteQr.x}" y="${layout.websiteQr.y}" width="${layout.websiteQr.size}" height="${layout.websiteQr.size}" fill="#eef2f5"/>`;
  const marks = certifications.map(certificationArtwork).join('');
  const testMark = isSandbox ? `<g data-field="test-mark">${outlineText('TEST', { centerX: 253, top: 6.5, width: 18, height: 4, referenceText: 'TEST' }, 'test')}</g>` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="366mm" height="17mm" viewBox="0 0 366 17"><title>${isSandbox ? 'TEST — ' : ''}Wave XL UID label, 366 x 17 mm${serviceTagNumber ? `, ${escapeXml(productNumber)}, SERVICE: ${escapeXml(serviceTagNumber)}` : ', draft preview'}</title><desc>${isSandbox ? 'TEST ONLY. No production device or GPV number is issued. ' : ''}UID label. Service QR contains only the service tag. Website QR: ${websiteUrl ? escapeXml(websiteUrl) : 'assigned when the device is saved'}. Selected certifications: ${escapeXml(certifications.filter(name => name !== 'None').join(', ') || 'none')}.</desc><rect width="366" height="17" fill="white"/>${fixed}${product}${service}${website}${marks}${testMark}</svg>`;
}

export function downloadUidSvg(svg, serviceTagNumber) {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${serviceTagNumber}.svg`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function printUidSvg(svg, serviceTagNumber) {
  const frame = document.createElement('iframe');
  frame.title = 'UID label print';
  frame.style.cssText = 'position:fixed;left:-10000px;width:420mm;height:297mm;border:0;';
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc || !frame.contentWindow) {
    frame.remove();
    throw new Error('The print preview could not be opened. Download the SVG instead.');
  }
  doc.open();
  doc.write(`<!doctype html><html><head><title>${escapeXml(serviceTagNumber)}</title><style>@page{size:A3 landscape;margin:27mm}html,body{margin:0;padding:0;background:white}svg{display:block;width:366mm;height:17mm;max-width:none;print-color-adjust:exact;-webkit-print-color-adjust:exact}</style></head><body>${svg}</body></html>`);
  doc.close();
  frame.contentWindow.onafterprint = () => frame.remove();
  window.setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
  }, 100);
  window.setTimeout(() => frame.remove(), 60000);
}
